/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useLocalizedErrorMessage } from '@/data/Localization';
import { ERROR_TYPE } from '@/data/constants/errors';
import {
	NotificationMessageOptionsType,
	NotificationText,
	NotificationsContext,
} from '@/data/context/notifications';
import { SessionErrorContext } from '@/data/context/sessionError';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ErrorType, SessionErrorType } from '@/data/types/Error';
import {
	handleForUserSessionError,
	handleLockOrderError,
	isOrderLockError,
} from '@/data/utils/customerService';
import { generateShippingInfoError } from '@/data/utils/generateShippingInfoError';
import { error as logError } from '@/data/utils/loggerUtil';
import { isErrorType } from '@/data/utils/processError';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { useCallback, useContext } from 'react';

export const useNotifications = () => {
	const { message, setMessage } = useContext(NotificationsContext);
	const { setSessionError } = useContext(SessionErrorContext);
	const getLocalizedErrorMessage = useLocalizedErrorMessage();

	const showMessage = useCallback(
		(message: NotificationText, options?: NotificationMessageOptionsType) => {
			setMessage({ text: message, ...options });
		},
		[setMessage]
	);

	const showErrorMessage = (message: NotificationText) => {
		setMessage({ text: message, severity: 'error' });
	};

	const showSnackbarMessage = (message: NotificationText) => {
		setMessage({ text: message, severity: 'snackbar' });
	};

	const showSuccessMessage = (text: NotificationText, title?: NotificationText) => {
		setMessage({ text, severity: 'success', title });
	};

	const showAddToCart = () => {
		setMessage({ text: '', isAddToCart: true });
	};

	const clearMessage = () => {
		setMessage({ text: '' });
	};

	const notifyError = useCallback(
		(
			error: TransactionErrorResponse | ErrorType | string,
			title?: string,
			changePassword?: boolean
		) => {
			if (changePassword) {
				setMessage({
					text: 'Código temporal que ha capturado no coincide con el código enviado a su correo. Verifique el código e inténtelo de nuevo.',
					severity: 'error',
					title: 'Ocurrió un problema',
				});
				return;
			}
			if (typeof error === 'string') {
				setMessage({ text: error, severity: 'error', title });
			} else if (isErrorType(error)) {
				if (
					(error.type === ERROR_TYPE.session && !handleForUserSessionError(error.messageKey)) ||
					error.errorMessage === `"The user does not have the authority to run this command "{0}"."`
				) {
					setSessionError(error as SessionErrorType);
				} else if (error.type === ERROR_TYPE.shippingInfo) {
					setMessage({
						severity: 'error',
						...generateShippingInfoError({ text: getLocalizedErrorMessage(error) }),
						title,
					});
				} else if (!(isOrderLockError(error.error) && handleLockOrderError(error.error))) {
					setMessage({ text: getLocalizedErrorMessage(error), severity: 'error', title });
				}
			} else {
				const message = getValueByPathFromUnknown(error, ['message']);
				setMessage({
					text: typeof message === 'string' ? message : 'Ocurrió un error inesperado',
					severity: 'error',
					title,
				});
				logError(undefined, 'Notifications: notifyError: error: %o', error);
			}
		},
		[getLocalizedErrorMessage, setMessage, setSessionError]
	);

	return {
		message,
		showAddToCart,
		showMessage,
		showSnackbarMessage,
		showSuccessMessage,
		showErrorMessage,
		clearMessage,
		notifyError,
	};
};
